import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../context/SocketContext';
import { formatImageUrl } from '../utils/imageUtils';

const CATEGORIES = ['men', 'women'];

const emptyForm = {
  name: '', brand: 'AURÉNE', category: 'women', subcategory: '',
  description: '', price: '', offerPercentage: 0, stock: '',
  images: '', sizes: '', colors: '', isVisible: true, isFeatured: false,
  priceType: 'expensive'
};

const emptyAdminForm = {
  firstName: '', lastName: '', email: '', password: '', confirmPassword: '', storeName: '', isActive: true
};

// Field component defined outside to prevent focus loss on re-renders
const Field = ({ label, name, form, setForm, type = 'text', as, children, ...props }) => (
  <div>
    <label className="label-sm text-lux-secondary block mb-1.5">{label}</label>
    {as === 'textarea' ? (
      <textarea
        value={form[name]}
        onChange={e => setForm({ ...form, [name]: e.target.value })}
        className="w-full border border-lux-border bg-white text-sm font-sans p-3 focus:outline-none focus:border-lux-primary resize-none"
        rows={4}
        {...props}
      />
    ) : as === 'select' ? (
      <select
        value={form[name]}
        onChange={e => setForm({ ...form, [name]: e.target.value })}
        className="w-full border border-lux-border bg-white text-sm font-sans p-3 focus:outline-none focus:border-lux-primary"
        {...props}
      >
        {children}
      </select>
    ) : (
      <input
        type={type}
        value={form[name]}
        onChange={e => setForm({ ...form, [name]: type === 'checkbox' ? e.target.checked : e.target.value })}
        className="w-full border border-lux-border bg-white text-sm font-sans p-3 focus:outline-none focus:border-lux-primary"
        {...props}
      />
    )}
  </div>
);

export default function SellerDashboard() {
  const { user, logout, isMainAdmin, isLocalAdmin, isSeller } = useAuth();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [tab, setTab] = useState('Overview');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [localAdmins, setLocalAdmins] = useState([]);
  const [customPool, setCustomPool] = useState([]);
  const [sellerReviews, setSellerReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ totalReviews: 0, averageRating: 0 });
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  
  const [adminForm, setAdminForm] = useState(emptyAdminForm);
  const [editingAdminId, setEditingAdminId] = useState(null);
  const [adminView, setAdminView] = useState('list'); // list, create, edit
  
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [expandedOrder, setExpandedOrder] = useState(null);

  const TABS = isMainAdmin 
    ? ['Overview', 'Products', 'Add Product', 'Orders', 'Reviews', 'Custom Pool', 'Local Admins']
    : isLocalAdmin 
      ? ['Overview', 'My Products', 'Add Product', 'Orders', 'Reviews', 'Custom Pool']
      : ['Overview', 'Products', 'Add Product', 'Orders', 'Reviews'];

  const fetchData = async () => {
    // Analytics
    api.get('/orders/analytics')
      .then(res => setAnalytics(res.data.analytics))
      .catch(err => console.error("Analytics error:", err));

    // Orders
    api.get('/orders/all')
      .then(res => setOrders(res.data.orders))
      .catch(err => console.error("Orders error:", err));

    // Products
    if (isMainAdmin) {
      api.get('/products/all-admin')
        .then(res => setProducts(res.data.products || []))
        .catch(err => console.error("Products error:", err));
    } else if (isLocalAdmin) {
      api.get('/products/my-products')
        .then(res => setProducts(res.data.products || []))
        .catch(err => console.error("Products error:", err));
    } else {
      api.get('/products')
        .then(res => setProducts(res.data.products || []))
        .catch(err => console.error("Products error:", err));
    }

    // Local Admins
    if (isMainAdmin) {
      api.get('/admin/local-admins')
        .then(res => setLocalAdmins(res.data.admins || []))
        .catch(err => console.error("Local Admins error:", err));
    }

    // Custom Pool
    if (isMainAdmin || isLocalAdmin) {
      api.get('/orders/custom-pool')
        .then(res => setCustomPool(res.data.orders || []))
        .catch(err => console.error("Custom Pool error:", err));
    }

    // Seller Reviews
    api.get('/reviews/seller')
      .then(res => {
        setSellerReviews(res.data.reviews || []);
        setReviewStats(res.data.stats || { totalReviews: 0, averageRating: 0 });
      })
      .catch(err => console.error("Reviews error:", err));
  };

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (!isSeller) { navigate('/'); return; }
    fetchData();
  }, [user, navigate, isSeller]);

  const handleLogout = () => { logout(); navigate('/login'); };
  
  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 3000);
  };

  // --- Product Management ---
  const handleToggleVisibility = async (product) => {
    try {
      await api.put(`/products/${product._id}`, { isVisible: !product.isVisible });
      fetchData(); // Refresh list
    } catch (err) { showMsg('error', 'Update failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`products/${id}`);
      fetchData();
    } catch (err) { showMsg('error', 'Delete failed'); }
  };

  const convertDriveLinks = (urls) => {
    return urls.map(url => {
      const match1 = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      if (match1) return `https://lh3.googleusercontent.com/d/${match1[1]}`;
      
      const match2 = url.match(/id=([a-zA-Z0-9_-]+)/);
      if (match2 && url.includes('drive.google.com')) return `https://lh3.googleusercontent.com/d/${match2[1]}`;
      
      return url;
    });
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const rawUrls = form.images ? form.images.split('\n').map(s => s.trim()).filter(Boolean) : [];
      const payload = {
        ...form,
        price: Number(form.price),
        stock: Number(form.stock),
        offerPercentage: Number(form.offerPercentage),
        images: convertDriveLinks(rawUrls),
        sizes: form.sizes ? form.sizes.split(',').map(s => s.trim()).filter(Boolean) : [],
        colors: form.colors ? form.colors.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      if (editingId) {
        await api.put(`products/${editingId}`, payload);
        showMsg('success', 'Product updated successfully');
      } else {
        await api.post('products', payload);
        showMsg('success', 'Product created successfully');
      }
      setForm(emptyForm);
      setEditingId(null);
      setTab(isLocalAdmin ? 'My Products' : 'Products');
      fetchData();
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Error saving product');
    }
    setLoading(false);
  };

  const handleEdit = (product) => {
    setForm({
      ...product,
      images: product.images?.join('\n') || '',
      sizes: product.sizes?.join(', ') || '',
      colors: product.colors?.join(', ') || '',
      priceType: product.priceType || 'expensive'
    });
    setEditingId(product._id);
    setTab('Add Product');
  };

  // --- Order Management ---
  const handleOrderStatus = async (id, status) => {
    try {
      await api.put(`orders/${id}/status`, { status });
      fetchData();
    } catch (err) { showMsg('error', 'Status update failed'); }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to remove this order record? This cannot be undone.')) return;
    try {
      await api.delete(`orders/${orderId}`);
      showMsg('success', 'Order removed successfully');
      fetchData();
    } catch (err) {
      console.error("Delete Error:", err.response);
      showMsg('error', err.response?.data?.message || 'Error removing order');
    }
  };

  const handleDownloadDesign = (designItem) => {
    if (!designItem.customThumbnail) {
      alert("Print-ready design not found for this item.");
      return;
    }
    const link = document.createElement('a');
    link.href = designItem.customThumbnail;
    link.download = `Design_${designItem.design || 'Custom'}_Fulfillment.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClaimOrder = async (orderId, itemId) => {
    try {
      await api.post(`/orders/${orderId}/item/${itemId}/claim`);
      showMsg('success', 'Order claimed successfully!');
      fetchData();
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Error claiming order');
    }
  };

  const handleRejectOrder = async (orderId, itemId) => {
    if (!window.confirm('Reject this order? It will be sent to the public pool for any seller to claim.')) return;
    try {
      await api.post(`/orders/${orderId}/item/${itemId}/reject`);
      showMsg('success', 'Order moved to public pool');
      fetchData();
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Error rejecting order');
    }
  };

  // --- Local Admin Management ---
  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (adminView === 'create' && adminForm.password !== adminForm.confirmPassword) {
      return showMsg('error', 'Passwords do not match');
    }
    setLoading(true);
    try {
      if (adminView === 'create') {
        const res = await api.post('/admin/local-admins', {
          firstName: adminForm.firstName,
          lastName: adminForm.lastName,
          email: adminForm.email,
          password: adminForm.password,
          storeName: adminForm.storeName
        });
        showMsg('success', res.data?.message || 'Local Admin created successfully');
      } else if (adminView === 'edit') {
        const payload = {
          firstName: adminForm.firstName,
          lastName: adminForm.lastName,
          email: adminForm.email,
          storeName: adminForm.storeName,
          isActive: adminForm.isActive
        };
        if (adminForm.password) payload.password = adminForm.password;
        await api.put(`/admin/local-admins/${editingAdminId}`, payload);
        showMsg('success', 'Local Admin updated successfully');
      }
      setAdminForm(emptyAdminForm);
      setEditingAdminId(null);
      setAdminView('list');
      fetchData();
    } catch (err) {
      showMsg('error', err.response?.data?.message || 'Error saving local admin');
    }
    setLoading(false);
  };

  const handleEditAdmin = (admin) => {
    setAdminForm({
      firstName: admin.firstName,
      lastName: admin.lastName,
      email: admin.email,
      storeName: admin.storeName,
      isActive: admin.isActive,
      password: '',
      confirmPassword: ''
    });
    setEditingAdminId(admin._id);
    setAdminView('edit');
  };

  const handleToggleAdminStatus = async (admin) => {
    try {
      await api.delete(`/admin/local-admins/${admin._id}`);
      fetchData();
      showMsg('success', 'Admin disabled successfully');
    } catch (err) { showMsg('error', err.response?.data?.message || 'Failed to update admin'); }
  };

  const handleEnableAdmin = async (admin) => {
    try {
      await api.put(`/admin/local-admins/${admin._id}`, { isActive: true });
      fetchData();
      showMsg('success', 'Admin enabled successfully');
    } catch (err) { showMsg('error', err.response?.data?.message || 'Failed to update admin'); }
  };

  return (
    <div className="min-h-screen bg-lux-bg">
      {/* Header */}
      <div className="bg-lux-primary text-white px-8 py-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <p className="label-sm text-lux-gold mb-1">
            {isMainAdmin ? 'Main Admin Portal' : 'Seller Portal'}
          </p>
          <h1 className="font-display text-3xl tracking-tight">
            {isMainAdmin ? 'AURÉNE Admin Dashboard' : isLocalAdmin ? 'AURÉNE Seller Dashboard' : 'AURÉNE India Dashboard'}
          </h1>
          <p className="font-sans text-sm text-white/60 mt-1">Welcome back, {user?.firstName}</p>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/')} className="text-xs font-sans tracking-widest uppercase py-2 px-4 border border-white/20 hover:border-lux-gold transition-all">
            Back to Site
          </button>
          <button onClick={handleLogout} className="text-xs font-sans tracking-widest uppercase py-2 px-4 bg-white text-lux-primary hover:bg-lux-gold hover:text-white transition-all">
            Sign Out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-8 border-b border-lux-border bg-white overflow-x-auto">
        <div className="flex gap-0 min-w-max">
          {TABS.map(t => (
            <button key={t} onClick={() => { setTab(t); if(t === 'Local Admins') setAdminView('list'); }}
              className={`px-6 py-4 text-xs font-sans tracking-widest uppercase border-b-2 transition-colors whitespace-nowrap ${tab === t ? 'border-lux-gold text-lux-primary font-medium' : 'border-transparent text-lux-secondary hover:text-lux-primary'}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {msg.text && (
        <div className={`mx-8 mt-4 px-4 py-3 text-xs font-sans ${msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {msg.text}
        </div>
      )}

      <div className="p-8">
        {/* Overview Tab */}
        {tab === 'Overview' && analytics && (
          <div className="space-y-8 animate-fade-in">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Total Revenue', value: `₹${analytics.totalRevenue?.toLocaleString('en-IN')}`, icon: '💰' },
                { label: 'Total Orders', value: analytics.totalOrders, icon: '📦' },
                { label: 'Products', value: analytics.totalProducts, icon: '👗' },
                { label: 'Out of Stock', value: analytics.outOfStock, icon: '⚠️' },
              ].map(stat => (
                <div key={stat.label} className="bg-white border border-lux-border p-6 shadow-sm">
                  <div className="text-2xl mb-2">{stat.icon}</div>
                  <p className="font-display text-3xl text-lux-primary">{stat.value}</p>
                  <p className="label-sm text-lux-secondary mt-1">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-lux-border p-6">
                <h3 className="font-display text-xl text-lux-primary mb-4">Recent Orders</h3>
                <div className="space-y-3">
                  {analytics.recentOrders?.map(o => (
                    <div key={o._id} className="flex justify-between items-center py-2 border-b border-lux-border last:border-0">
                      <div>
                        <p className="text-xs font-sans text-lux-primary font-medium">{o.orderNumber || o._id.substring(0, 8)}</p>
                        <p className="text-[10px] text-lux-secondary">{o.userId?.firstName} {o.userId?.lastName}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-sans font-medium">₹{o.totalAmount?.toLocaleString('en-IN')}</p>
                        <p className="text-[10px] uppercase text-lux-gold">{o.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-lux-border p-6">
                <h3 className="font-display text-xl text-lux-primary mb-4">Top Products</h3>
                <div className="space-y-3">
                  {analytics.topProducts?.map(p => (
                    <div key={p._id} className="flex justify-between items-center py-2 border-b border-lux-border last:border-0">
                      <p className="text-sm font-sans text-lux-primary truncate pr-4">{p._id}</p>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-sans font-medium">{p.totalSold} sold</p>
                        <p className="text-[10px] text-lux-secondary">₹{p.revenue?.toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Products / My Products Tab */}
        {(tab === 'Products' || tab === 'My Products') && (
          <div className="animate-fade-in">
            <div className="bg-white border border-lux-border overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-lux-border bg-lux-bg/50">
                    <th className="p-4 label-sm text-lux-secondary">Product</th>
                    {isMainAdmin && <th className="p-4 label-sm text-lux-secondary">Seller</th>}
                    <th className="p-4 label-sm text-lux-secondary">Price</th>
                    <th className="p-4 label-sm text-lux-secondary">Stock</th>
                    <th className="p-4 label-sm text-lux-secondary">Status</th>
                    <th className="p-4 label-sm text-lux-secondary text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(p => (
                    <tr key={p._id} className="border-b border-lux-border hover:bg-lux-bg/30">
                      <td className="p-4">
                        <div className="flex items-center gap-4">
                          <img src={formatImageUrl(p.images?.[0]) || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&q=80'} alt={p.name} className="w-10 h-14 object-cover bg-gray-100" />
                          <div>
                            <p className="font-sans text-sm font-medium text-lux-primary line-clamp-1">{p.name}</p>
                            <p className="text-[10px] text-lux-secondary uppercase tracking-widest">{p.brand} · {p.category}</p>
                            {p.priceType === 'affordable' && (
                              <span className="inline-block mt-1 bg-emerald-50 text-emerald-700 text-[9px] px-1.5 py-0.5 border border-emerald-200 uppercase tracking-widest">Affordable</span>
                            )}
                          </div>
                        </div>
                      </td>
                      {isMainAdmin && (
                        <td className="p-4 font-sans text-sm text-lux-secondary">
                          {p.createdBy?.storeName || (p.createdBy ? `${p.createdBy.firstName} ${p.createdBy.lastName}` : '-')}
                        </td>
                      )}
                      <td className="p-4 font-sans text-sm">
                        ₹{p.finalPrice?.toLocaleString('en-IN')}
                        {p.offerPercentage > 0 && <span className="text-[10px] text-lux-gold ml-2">-{p.offerPercentage}%</span>}
                      </td>
                      <td className="p-4 font-sans text-sm text-lux-secondary">{p.stock}</td>
                      <td className="p-4">
                        <button onClick={() => handleToggleVisibility(p)}
                          className={`text-xs px-3 py-1 font-sans ${p.isVisible ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                          {p.isVisible ? 'Visible' : 'Hidden'}
                        </button>
                      </td>
                      <td className="p-4 text-right space-x-3">
                        <button onClick={() => handleEdit(p)} className="text-xs font-sans text-lux-gold hover:text-lux-primary">Edit</button>
                        <button onClick={() => handleDelete(p._id)} className="text-xs font-sans text-red-500 hover:text-red-700">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add Product Tab */}
        {tab === 'Add Product' && (
          <div className="max-w-4xl animate-fade-in">
            <form onSubmit={handleSubmit} className="bg-white border border-lux-border p-8 space-y-8">
              <div className="flex justify-between items-center border-b border-lux-border pb-4">
                <h2 className="font-display text-2xl text-lux-primary">{editingId ? 'Edit Product' : 'New Product'}</h2>
                {editingId && (
                  <button type="button" onClick={() => { setForm(emptyForm); setEditingId(null); }} className="text-xs font-sans text-lux-secondary hover:text-lux-primary">Cancel Edit</button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Product Name *" name="name" form={form} setForm={setForm} required />
                <Field label="Brand *" name="brand" form={form} setForm={setForm} required />
                
                <Field label="Category *" name="category" as="select" form={form} setForm={setForm}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
                </Field>
                <Field label="Subcategory" name="subcategory" form={form} setForm={setForm} placeholder="e.g. Shirts, Dresses" />

                <Field label="Price (₹) *" name="price" type="number" min="0" form={form} setForm={setForm} required />
                <Field label="Offer Percentage (%)" name="offerPercentage" type="number" min="0" max="100" form={form} setForm={setForm} />
                
                <Field label="Stock Quantity *" name="stock" type="number" min="0" form={form} setForm={setForm} required />
                
                <div>
                  <label className="label-sm text-lux-secondary block mb-1.5">Price Category *</label>
                  <div className="flex gap-6 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="priceType" value="expensive" checked={form.priceType === 'expensive'} onChange={() => setForm({...form, priceType: 'expensive'})} className="accent-lux-gold" />
                      <span className="text-sm font-sans text-lux-primary">Expensive</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="priceType" value="affordable" checked={form.priceType === 'affordable'} onChange={() => setForm({...form, priceType: 'affordable'})} className="accent-lux-gold" />
                      <span className="text-sm font-sans text-lux-primary">Affordable</span>
                    </label>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Field label="Description *" name="description" as="textarea" form={form} setForm={setForm} required />
                </div>
                
                <div className="md:col-span-2">
                  <Field label="Image URLs (one per line) *" name="images" as="textarea" rows={3} form={form} setForm={setForm} required placeholder="https://example.com/img1.jpg&#10;https://example.com/img2.jpg" />
                </div>
                
                <Field label="Sizes (comma separated)" name="sizes" form={form} setForm={setForm} placeholder="S, M, L, XL" />
                <Field label="Colors (comma separated)" name="colors" form={form} setForm={setForm} placeholder="Black, White, Gold" />

                <div className="md:col-span-2 flex gap-8 py-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.isVisible} onChange={e => setForm({...form, isVisible: e.target.checked})} className="accent-lux-gold w-4 h-4" />
                    <span className="text-sm font-sans text-lux-primary">Visible to customers</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.isFeatured} onChange={e => setForm({...form, isFeatured: e.target.checked})} className="accent-lux-gold w-4 h-4" />
                    <span className="text-sm font-sans text-lux-primary">Feature on homepage</span>
                  </label>
                </div>
              </div>

              <div className="pt-4 border-t border-lux-border">
                <button type="submit" disabled={loading} className="btn-primary w-full md:w-auto">
                  {loading ? 'Saving...' : editingId ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Orders Tab */}
        {tab === 'Orders' && (
          <div className="animate-fade-in">
            <div className="space-y-4">
              {orders.map(order => (
                <div key={order._id} className="bg-white border border-lux-border p-6">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-lux-border pb-4 mb-4">
                    <div>
                      <p className="label-sm text-lux-gold mb-1">Order #{order.orderNumber || order._id.substring(0, 8)}</p>
                      <p className="text-sm font-sans text-lux-secondary">
                        {new Date(order.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                      <p className="text-sm font-sans text-lux-primary font-medium mt-1">
                        {order.userId?.firstName} {order.userId?.lastName} ({order.userId?.email})
                      </p>
                    </div>
                    <div className="flex flex-col md:items-end gap-2">
                      <p className="font-display text-2xl text-lux-primary">₹{isLocalAdmin ? order.filteredTotal?.toLocaleString('en-IN') : order.totalAmount?.toLocaleString('en-IN')}</p>
                      <div className="flex items-center gap-3">
                        <select
                          value={order.status}
                          onChange={(e) => handleOrderStatus(order._id, e.target.value)}
                          className="text-xs font-sans p-2 border border-lux-border bg-lux-bg focus:outline-none"
                        >
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                        
                        {/* Only main_admin can delete orders for safety */}
                        {isMainAdmin && (
                          <button
                            onClick={() => handleDeleteOrder(order._id)}
                            className="text-xs px-3 py-2 text-red-600 bg-red-50 border border-red-100 hover:bg-red-100 transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {order.products.map((item, idx) => (
                      <div key={idx} className="flex gap-4 items-center p-3 bg-lux-bg/50">
                        <img src={formatImageUrl(item.image) || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&q=80'} alt={item.name} className="w-12 h-16 object-cover bg-white" />
                        <div className="flex-1">
                          <p className="text-sm font-sans font-medium text-lux-primary">{item.name}</p>
                          <p className="text-xs text-lux-secondary">{item.brand} | Qty: {item.quantity} {item.size && `| Size: ${item.size}`} {item.color && `| Color: ${item.color}`}</p>
                          
                          {item.isCustom && (
                            <div className="mt-2 flex gap-3">
                              <span className="text-[10px] bg-lux-gold text-white px-2 py-0.5 tracking-wider uppercase">Custom Design</span>
                              <button
                                onClick={() => handleDownloadDesign(item)}
                                className="text-[10px] text-lux-gold border border-lux-gold px-2 py-0.5 tracking-wider uppercase hover:bg-lux-gold hover:text-white transition-colors"
                              >
                                Download Design
                              </button>
                            </div>
                          )}
                        </div>
                        <p className="text-sm font-sans font-medium">₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-lux-border flex flex-col md:flex-row justify-between items-start gap-4">
                    <div>
                      <p className="label-sm text-lux-secondary mb-2">Shipping Details</p>
                      <p className="text-sm font-sans font-semibold text-lux-primary">
                        {order.shippingAddress?.fullName || `${order.userId?.firstName || ''} ${order.userId?.lastName || ''}`.trim()}
                      </p>
                      <p className="text-sm font-sans text-lux-secondary">{order.shippingAddress?.address || 'No street address'}</p>
                      <p className="text-sm font-sans text-lux-secondary">
                        {[order.shippingAddress?.city, order.shippingAddress?.state, order.shippingAddress?.zipCode].filter(Boolean).join(', ')}
                      </p>
                      <p className="text-sm font-sans text-lux-secondary">{order.shippingAddress?.country || 'India'}</p>
                    </div>

                    {/* Live Delivery Pin on Google Maps for Admin / Seller navigation */}
                    {order.shippingAddress?.latitude && order.shippingAddress?.longitude ? (
                      <div className="bg-lux-bg/80 border border-lux-gold/40 p-4 rounded-sm min-w-[240px] flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-base">📍</span>
                            <p className="text-[11px] font-sans font-bold text-lux-primary uppercase tracking-wider">Live Delivery Pin</p>
                          </div>
                          <p className="text-[11px] font-mono text-lux-secondary">
                            {Number(order.shippingAddress.latitude).toFixed(5)}° N, {Number(order.shippingAddress.longitude).toFixed(5)}° E
                          </p>
                        </div>
                        <a
                          href={`https://www.google.com/maps?q=${order.shippingAddress.latitude},${order.shippingAddress.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 inline-flex items-center justify-center gap-2 bg-lux-primary text-white text-[10px] font-sans uppercase tracking-widest px-3 py-2 hover:bg-lux-gold transition-colors font-semibold"
                        >
                          <span>Open in Google Maps 🗺️</span>
                        </a>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 p-3 text-[11px] text-gray-500 font-sans">
                        📍 Map pin coordinates not selected
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {orders.length === 0 && (
                <div className="bg-white border border-lux-border p-12 text-center">
                  <p className="font-display text-2xl text-lux-secondary">No orders found</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Custom Pool Tab */}
        {tab === 'Custom Pool' && (
          <div className="animate-fade-in">
            <div className="space-y-4">
              {customPool.map(order => (
                <div key={order._id} className="bg-white border border-lux-border p-6">
                  <div className="border-b border-lux-border pb-4 mb-4">
                    <p className="label-sm text-lux-gold mb-1">Order #{order.orderNumber || order._id.substring(0, 8)}</p>
                    <p className="text-sm font-sans text-lux-secondary">
                      {new Date(order.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-sm font-sans text-lux-primary font-medium mt-1">
                      {order.userId?.firstName} {order.userId?.lastName} ({order.userId?.email})
                    </p>
                  </div>

                  <div className="space-y-3">
                    {order.products.map((item, idx) => (
                      <div key={idx} className="flex flex-col md:flex-row gap-4 items-start md:items-center p-3 bg-lux-bg/50 border border-transparent hover:border-lux-border transition-colors">
                        <img src={formatImageUrl(item.customThumbnail || item.image) || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&q=80'} alt={item.name} className="w-16 h-20 object-cover bg-white border border-lux-border" />
                        <div className="flex-1">
                          <p className="text-sm font-sans font-medium text-lux-primary">{item.name}</p>
                          <p className="text-xs text-lux-secondary">Qty: {item.quantity} {item.size && `| Size: ${item.size}`} {item.color && `| Color: ${item.color}`}</p>
                          {item.comments && (
                            <p className="text-xs text-lux-primary mt-1 italic">"{item.comments}"</p>
                          )}
                          
                          {item.preferredSeller === user._id && (
                            <span className="inline-block mt-2 text-[10px] bg-lux-gold/20 text-lux-gold font-bold px-2 py-0.5 tracking-wider uppercase">Preferred Partner</span>
                          )}
                        </div>
                        
                        <div className="flex flex-col gap-2 min-w-[140px]">
                          <button
                            onClick={() => handleClaimOrder(order._id, item._id)}
                            className="text-[10px] font-bold uppercase tracking-widest text-white bg-lux-primary hover:bg-lux-gold transition-colors py-2 px-4 w-full"
                          >
                            Accept Order
                          </button>
                          
                          {item.preferredSeller === user._id && (
                            <button
                              onClick={() => handleRejectOrder(order._id, item._id)}
                              className="text-[10px] font-bold uppercase tracking-widest text-red-600 border border-red-200 hover:bg-red-50 transition-colors py-2 px-4 w-full"
                            >
                              Reject to Pool
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {customPool.length === 0 && (
                <div className="bg-white border border-lux-border p-12 text-center">
                  <p className="font-display text-2xl text-lux-secondary">No custom orders available</p>
                  <p className="text-sm font-sans text-lux-secondary mt-2">Check back later for new custom design requests.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reviews Tab */}
        {tab === 'Reviews' && (
          <div className="animate-fade-in space-y-6">
            {/* Reviews Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white border border-lux-border p-6 text-center">
                <p className="font-display text-4xl text-lux-primary">{reviewStats.averageRating || '—'}</p>
                <div className="flex justify-center gap-0.5 my-2">
                  {[1,2,3,4,5].map(s => (
                    <svg key={s} width="16" height="16" viewBox="0 0 24 24"
                      fill={s <= Math.round(reviewStats.averageRating) ? '#C6A75E' : 'none'}
                      stroke={s <= Math.round(reviewStats.averageRating) ? '#C6A75E' : '#D1CBC0'}
                      strokeWidth="1.5">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  ))}
                </div>
                <p className="label-sm text-lux-secondary">Average Rating</p>
              </div>
              <div className="bg-white border border-lux-border p-6 text-center">
                <p className="font-display text-4xl text-lux-primary">{reviewStats.totalReviews}</p>
                <p className="label-sm text-lux-secondary mt-2">Total Reviews</p>
              </div>
              <div className="bg-white border border-lux-border p-6 text-center">
                <p className="font-display text-4xl text-lux-primary">
                  {sellerReviews.filter(r => !r.sellerReply).length}
                </p>
                <p className="label-sm text-lux-secondary mt-2">Awaiting Reply</p>
              </div>
            </div>

            {/* Reviews List */}
            {sellerReviews.length === 0 ? (
              <div className="bg-white border border-lux-border p-12 text-center">
                <p className="font-display text-2xl text-lux-secondary">No reviews yet</p>
                <p className="text-sm font-sans text-lux-secondary mt-2">Reviews from customers will appear here.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sellerReviews.map(review => (
                  <div key={review._id} className="bg-white border border-lux-border p-6">
                    <div className="flex flex-col md:flex-row gap-4">
                      {/* Product thumbnail */}
                      <img
                        src={formatImageUrl(review.product?.images?.[0]) || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&q=80'}
                        alt={review.product?.name}
                        className="w-14 h-18 object-cover bg-gray-100 border border-lux-border flex-shrink-0"
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&q=80'; }}
                      />
                      <div className="flex-1">
                        {/* Header */}
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-sm font-sans font-medium text-lux-primary">
                            {review.user?.firstName} {review.user?.lastName}
                          </span>
                          <span className="text-[9px] font-sans tracking-widest uppercase bg-green-50 text-green-700 px-1.5 py-0.5 border border-green-200">
                            ✓ Verified
                          </span>
                          <span className="text-[10px] font-sans text-lux-secondary ml-auto">
                            {new Date(review.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        {/* Product name */}
                        <p className="text-[10px] text-lux-secondary uppercase tracking-widest mb-2">
                          {review.product?.brand} · {review.product?.name}
                        </p>
                        {/* Stars */}
                        <div className="flex gap-0.5 mb-2">
                          {[1,2,3,4,5].map(s => (
                            <svg key={s} width="14" height="14" viewBox="0 0 24 24"
                              fill={s <= review.rating ? '#C6A75E' : 'none'}
                              stroke={s <= review.rating ? '#C6A75E' : '#D1CBC0'}
                              strokeWidth="1.5">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                            </svg>
                          ))}
                        </div>
                        {/* Title & Comment */}
                        {review.title && <p className="text-sm font-sans font-semibold text-lux-primary mb-1">{review.title}</p>}
                        {review.comment && <p className="text-sm font-sans text-lux-secondary leading-relaxed">{review.comment}</p>}

                        {/* Review images */}
                        {review.images?.length > 0 && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {review.images.map((img, idx) => (
                              <img key={idx} src={formatImageUrl(img)} alt="Review"
                                className="w-12 h-12 object-cover border border-lux-border"
                                onError={(e) => { e.target.style.display = 'none'; }} />
                            ))}
                          </div>
                        )}

                        {/* Existing reply */}
                        {review.sellerReply && replyingTo !== review._id && (
                          <div className="mt-3 pl-4 border-l-2 border-lux-gold bg-lux-bg/50 p-3">
                            <p className="text-[10px] font-sans tracking-widest uppercase text-lux-gold mb-1">Your Reply</p>
                            <p className="text-sm font-sans text-lux-secondary">{review.sellerReply}</p>
                          </div>
                        )}

                        {/* Reply UI */}
                        {replyingTo === review._id ? (
                          <div className="mt-3 space-y-2">
                            <textarea
                              value={replyText}
                              onChange={e => setReplyText(e.target.value)}
                              className="w-full border border-lux-border bg-white text-sm font-sans p-3 focus:outline-none focus:border-lux-primary resize-none"
                              rows={3}
                              placeholder="Write your reply to this customer..."
                              maxLength={1000}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    await api.put(`/reviews/${review._id}/reply`, { reply: replyText });
                                    showMsg('success', 'Reply saved');
                                    setReplyingTo(null);
                                    setReplyText('');
                                    fetchData();
                                  } catch (err) {
                                    showMsg('error', err.response?.data?.message || 'Failed to save reply');
                                  }
                                }}
                                disabled={!replyText.trim()}
                                className="text-[10px] font-bold uppercase tracking-widest bg-lux-primary text-white py-2 px-4 hover:bg-lux-gold transition-colors disabled:opacity-50"
                              >
                                Submit Reply
                              </button>
                              <button
                                onClick={() => { setReplyingTo(null); setReplyText(''); }}
                                className="text-[10px] font-bold uppercase tracking-widest text-lux-secondary border border-lux-border py-2 px-4 hover:bg-lux-bg transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setReplyingTo(review._id); setReplyText(review.sellerReply || ''); }}
                            className="mt-3 text-[10px] font-bold uppercase tracking-widest text-lux-gold hover:text-lux-primary transition-colors"
                          >
                            {review.sellerReply ? 'Edit Reply' : 'Reply to Review'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Local Admins Tab (MAIN ADMIN ONLY) */}
        {tab === 'Local Admins' && isMainAdmin && (
          <div className="animate-fade-in">
            {adminView === 'list' && (
              <div className="bg-white border border-lux-border">
                <div className="flex justify-between items-center p-6 border-b border-lux-border">
                  <h2 className="font-display text-2xl text-lux-primary">Local Admins</h2>
                  <button onClick={() => setAdminView('create')} className="btn-primary">
                    + Create Local Admin
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-lux-border bg-lux-bg/50">
                        <th className="p-4 label-sm text-lux-secondary">Name</th>
                        <th className="p-4 label-sm text-lux-secondary">Email</th>
                        <th className="p-4 label-sm text-lux-secondary">Store Name</th>
                        <th className="p-4 label-sm text-lux-secondary">Status</th>
                        <th className="p-4 label-sm text-lux-secondary text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {localAdmins.map(admin => (
                        <tr key={admin._id} className="border-b border-lux-border hover:bg-lux-bg/30">
                          <td className="p-4 font-sans text-sm font-medium text-lux-primary">
                            {admin.firstName} {admin.lastName}
                          </td>
                          <td className="p-4 font-sans text-sm text-lux-secondary">{admin.email}</td>
                          <td className="p-4 font-sans text-sm text-lux-secondary">{admin.storeName || '-'}</td>
                          <td className="p-4">
                            <span className={`text-[10px] px-2 py-0.5 uppercase tracking-widest ${admin.isActive ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                              {admin.isActive ? 'Active' : 'Disabled'}
                            </span>
                          </td>
                          <td className="p-4 text-right space-x-3">
                            <button onClick={() => handleEditAdmin(admin)} className="text-xs font-sans text-lux-gold hover:text-lux-primary">Edit</button>
                            {admin.isActive ? (
                              <button onClick={() => handleToggleAdminStatus(admin)} className="text-xs font-sans text-red-500 hover:text-red-700">Disable</button>
                            ) : (
                              <button onClick={() => handleEnableAdmin(admin)} className="text-xs font-sans text-green-600 hover:text-green-800">Enable</button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {localAdmins.length === 0 && (
                        <tr>
                          <td colSpan="5" className="p-8 text-center text-lux-secondary font-sans">No local admins found.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(adminView === 'create' || adminView === 'edit') && (
              <div className="max-w-3xl mx-auto bg-white border border-lux-border p-8">
                <div className="flex justify-between items-center border-b border-lux-border pb-4 mb-6">
                  <h2 className="font-display text-2xl text-lux-primary">
                    {adminView === 'create' ? 'Create / Promote Local Admin' : 'Edit Local Admin'}
                  </h2>
                  <button onClick={() => { setAdminView('list'); setAdminForm(emptyAdminForm); setEditingAdminId(null); }} className="text-xs font-sans text-lux-secondary hover:text-lux-primary">
                    Cancel & Back
                  </button>
                </div>

                {adminView === 'create' && (
                  <div className="mb-6 p-4 bg-amber-50/70 border border-amber-200/80 rounded-sm">
                    <p className="text-xs font-sans text-amber-800 leading-relaxed">
                      💡 <strong>Smart Role Upgrade:</strong> If the email address belongs to an existing customer, submitting this form will seamlessly promote their account to <strong>Local Admin</strong> — preserving their existing password, login, and order history!
                    </p>
                  </div>
                )}
                
                <form onSubmit={handleAdminSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label="First Name *" name="firstName" form={adminForm} setForm={setAdminForm} required />
                    <Field label="Last Name *" name="lastName" form={adminForm} setForm={setAdminForm} required />
                    <Field label="Email Address *" type="email" name="email" form={adminForm} setForm={setAdminForm} required />
                    <Field label="Store Name" name="storeName" form={adminForm} setForm={setAdminForm} placeholder="Public display name" />
                    
                    <div className="md:col-span-2 pt-4 border-t border-lux-border">
                      <p className="label-sm text-lux-secondary mb-4">
                        {adminView === 'create' ? 'Security Details' : 'Change Password (Leave blank to keep current)'}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Field label="Password *" type="password" name="password" form={adminForm} setForm={setAdminForm} required={adminView === 'create'} minLength="6" />
                        <Field label="Confirm Password *" type="password" name="confirmPassword" form={adminForm} setForm={setAdminForm} required={adminView === 'create'} minLength="6" />
                      </div>
                    </div>

                    {adminView === 'edit' && (
                      <div className="md:col-span-2 pt-4 border-t border-lux-border">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={adminForm.isActive} onChange={e => setAdminForm({...adminForm, isActive: e.target.checked})} className="accent-lux-gold w-4 h-4" />
                          <span className="text-sm font-sans text-lux-primary">Account is Active</span>
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="pt-6 text-right">
                    <button type="submit" disabled={loading} className="btn-primary">
                      {loading ? 'Saving...' : adminView === 'create' ? 'Create Local Admin' : 'Update Local Admin'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}